import { useEffect, useMemo } from 'react'
import { faStar } from '@fortawesome/pro-solid-svg-icons'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { Card, Icon, Image, List, Text, Button, Badge } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { usePlanChangeEntry } from '@/features/user/settings/sections/plan-change/lib/usePlanChangeEntry'
import { ComparePlansLink } from './ComparePlansLink'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsBasicPlan } from '@/reducers/subscription.slice'
import { getFinancialProfile } from '@/selectors/user.selectors'
import { selectCurrentAnnualTaxDetails } from '@/v2-features/admin/annual-tax-details/annualTaxDetails.selector'
import { TAX_ENTITY_TYPES } from '../taxConstants'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/v2-features/admin/annual-tax-details/annualTaxDetails.slice'
import { fetchFinancialProfileIfNeeded } from '@/actions/financialProfileActions'

const Perk = ({ text }: { text: string }) => (
  <List.Item className="flex items-center -mb-2">
    <Icon color="text-green-50" icon={faCheck} className="mr-2" size="xl" />
    <Text variant={TypographyStyles.BODY_SM}>{text}</Text>
  </List.Item>
)

export const UpgradeToPremiumCard = () => {
  const dispatch = useAppDispatch()

  const isBasicPlan = useReselector(selectIsBasicPlan)
  const financialProfile = useReselector(getFinancialProfile)
  const taxDetails = useReselector(selectCurrentAnnualTaxDetails)
  const taxCutoffDate = useMemo(() => {
    if (financialProfile?.taxEntityType === TAX_ENTITY_TYPES.form_1120_s) {
      return convertUtcToLocalDate(
        taxDetails?.form_1120_s_new_user_tax_cutoff_date
      )?.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)
    }
    return convertUtcToLocalDate(
      taxDetails?.form_1040_new_user_tax_cutoff_date
    )?.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)
  }, [financialProfile?.taxEntityType, taxDetails])
  const lateJoinerDate = useMemo(
    () =>
      convertUtcToLocalDate(taxDetails?.new_user_cutoff_date)
        ?.minus({ days: 1 })
        ?.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG),
    [taxDetails]
  )

  const { goToPlanChange } = usePlanChangeEntry()

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
    dispatch(fetchFinancialProfileIfNeeded())
  }, [dispatch])

  return (
    <Card className="p-4 bg-[linear-gradient(180deg,rgba(252,241,202,0.90)_0%,rgba(232,239,230,0)_100%)]">
      <Badge
        variant="secondary"
        color="green"
        icon={faStar}
        className="w-full flex"
      >
        Limited time offer: Upgrade by {taxCutoffDate} for full{' '}
        {taxDetails?.taxYear} tax support. If you upgrade after {lateJoinerDate}
        , you&apos;ll likely need to file an extension.
      </Badge>
      <div className="flex gap-10 mt-6 flex-row">
        <div className="flex flex-col flex-[1] items-center">
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/annual_taxes_compass.svg"
            alt="Annual Taxes"
            className="w-[200px] h-[200px]"
          />
          <div className="mt-16">
            <Button onClick={goToPlanChange}>Upgrade your Plan</Button>
          </div>
          <Text variant={TypographyStyles.BODY_SM} className="mt-4">
            Note: Once you upgrade, you cannot downgrade to your current plan.
          </Text>
        </div>

        <div className="flex-[2]">
          <Text variant={TypographyStyles.DISPLAY_MD}>
            Upgrade and say goodbye to tax stress
          </Text>
          <Text
            variant={TypographyStyles.BODY_SM}
            color={TextColors.MUTED}
            className="mt-2"
          >
            Get everything in {isBasicPlan ? 'basic' : 'starter'} plan, plus
            access to special tax season offers:
          </Text>
          <List className="-ml-4">
            <Perk text="Free tax extension filing support (as needed)" />
            <Perk text="Personal and business income tax prep and filing" />
            <Perk text="Year-round tax guidance" />
            {!isBasicPlan && (
              <Perk text="20% off annual pricing for Starter Plan members" />
            )}
          </List>
          <ComparePlansLink />
        </div>
      </div>
    </Card>
  )
}
